import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { AutomationsV1AutomationExecutionServiceGetAutomationExecution, StepupV1StepUpTransactionServiceGet } from 'c1gen/permissions'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { ExternalLink } from 'components/core/links/external-link'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { C1MultiUserDisplay } from 'components/core/multi-user/C1MultiUserDisplay'
import { GrantTaskIcon } from 'components/core/task/task-type'
import { C1TextWithLineClamp } from 'components/core/TextWithTruncation/C1TextWithLineClamp'
import { RelativeTimestamp } from 'components/core/time/display/index'
import { RoundedDuration } from 'components/core/time/duration/duration'
import { useHasPermission } from 'components/no-ui/capability'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { StepUpTransactionFetcher } from 'components/pages/step-up/StepUpTransactionFetcher'
import { TaskAuditStyles } from 'components/pages/task/audit/styles'
import { dateSort } from 'components/pages/task/details/task-content-v2/common/TaskUtil'
import { TaskStateTypography } from 'components/pages/task/state'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { stringFromTaskAction, TaskValue } from 'lib/actions/task'
import { UserValue } from 'lib/actions/user'
import { routes, unsafeRoute } from 'lib/routes'
import { Ban, CircleAlert, CircleCheck } from 'lucide-react'
import { AppEntitlementExpandMask } from 'pbts/c1/api/app/v1/app_entitlement'
import { AutomationExecutionExpandMask, GetAutomationExecutionRequest } from 'pbts/c1/api/automations/v1/automations_executions'
import { ApprovalInstance, ApprovalInstanceState } from 'pbts/c1/api/policy/v1/policy_steps_approval'
import { TaskAuditEventType, TaskAuditListRequest, TaskAuditView } from 'pbts/c1/api/task/v1/audit'
import { AccessRequestOutcome, CertifyOutcome, GrantOutcome, RevokeOutcome, Task, TaskType } from 'pbts/c1/api/task/v1/task'
import { Timestamp as pbtsTimestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { getAppEntitlementApi } from 'redux_toolkit/entitlements'
import { SerializedErrorType } from 'redux_toolkit/lib/reducer'
import { TaskAuditApi } from 'redux_toolkit/tasks'
import { getWorkflowExecutionApi } from 'redux_toolkit/workflows'

const AuditFormatMessage: FC<{ taskType?: TaskType['taskType']; instance?: ApprovalInstance }> = ({
    taskType,
    instance,
}) => {
    switch (instance?.state) {
        case ApprovalInstanceState.INIT:
            switch (taskType?.oneofKind) {
                case 'grant':
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="beginning approval step"
                        />
                    )
                case 'revoke':
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="beginning revocation step"
                        />
                    )
                case 'certify':
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="beginning certification step"
                        />
                    )
                default:
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="beginning step"
                        />
                    )
            }
        case ApprovalInstanceState.SENDING_NOTIFICATIONS:
            switch (taskType?.oneofKind) {
                case 'grant':
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="sent approval notifications"
                        />
                    )
                case 'revoke':
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="sent revocation notifications"
                        />
                    )
                case 'certify':
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="sent certification notifications"
                        />
                    )
                default:
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="sent notifications"
                        />
                    )
            }
        case ApprovalInstanceState.WAITING:
            return <FormattedMessage id="component_task_details_audit_row_reason" defaultMessage="is waiting" />
        case ApprovalInstanceState.DONE:
            switch (instance?.outcome.oneofKind) {
                case 'approved':
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="approved access"
                        />
                    )
                case 'denied':
                    if (taskType?.oneofKind !== 'certify') {
                        return (
                            <FormattedMessage
                                id="component_task_details_audit_row_reason"
                                defaultMessage="denied access"
                            />
                        )
                    }
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="access decertified"
                        />
                    )
                default:
                    return (
                        <FormattedMessage
                            id="component_task_details_audit_row_reason"
                            defaultMessage="completed approval step"
                        />
                    )
            }

        default:
            return (
                <FormattedMessage
                    id="component_task_details_audit_row_reason"
                    defaultMessage="changed approval step instance"
                />
            )
    }
}

const ActionExecutionLink: FC<{ executionId: number }> = ({ executionId }): React.ReactElement | null => {
    const hasPermission = useHasPermission(AutomationsV1AutomationExecutionServiceGetAutomationExecution)
    const { data: execution, isLoading } = getWorkflowExecutionApi.useGetAutomationExecution(
        GetAutomationExecutionRequest.create({
            id: executionId,
            expandMask: AutomationExecutionExpandMask.create({
                paths: ['*'],
            }),
        }),
        {
            notify: false,
            actionable: hasPermission,
        },
    )
    const workflowId = execution?.automationTemplateId

    if (!hasPermission || isLoading || !workflowId) {
        return null
    }

    const href = unsafeRoute(`/admin/automations/edit/${workflowId}?execution_id=${executionId}`)
    return (
        <C1Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline', color: 'inherit' }}
        >
            <FormattedMessage id="workflow_execution_view_details" defaultMessage="View workflow execution" />
        </C1Link>
    )
}

const AuditEntitlementDisplay: FC<{
    entitlementErr: Readonly<SerializedErrorType> | undefined,
    entitlement: Readonly<AppEntitlementValue> | undefined,
    topPadding?: boolean,
}> = ({ entitlementErr, entitlement, topPadding }) => {
    if (!entitlement) {
        return null
    }
    if (entitlementErr) {
        return null
    }

    return <Box sx={topPadding ? { paddingLeft: 2, paddingTop: 2 } : { paddingLeft: 1 }}>
        {entitlementErr && <APIError err={entitlementErr} />}
        {entitlement && <C1EntitlementDisplayName entitlement={entitlement} readOnly />}
    </Box>
}

export const AuditEvent: FC<{ task: Task; audit: TaskAuditView; }> = ({ audit, task }) => {
    const user: UserValue | undefined = audit.metadata?.user
    const hasStepUpTransactionPermission = useHasPermission(StepupV1StepUpTransactionServiceGet)

    if (!user) {
        return <Skeleton />
    }

    const eventType = audit.typ.oneofKind
    switch (eventType) {
        case 'comment': {
            const comment = audit.typ.comment.comment

            return <Stack display="flex" alignItems="top" justifyContent="flex-start" direction="row">
                <Box>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                </Box>
                <Box ml={1} mt="3px" flex={1}>
                    <C1TextWithLineClamp lineClamp={2}>
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_comment"
                            defaultMessage="commented: {comment}"
                            values={{ comment }}
                        />
                    </C1TextWithLineClamp>
                </Box>
            </Stack>
        }
        case 'stateChange':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Box sx={TaskAuditStyles.stepText}>
                        <TaskStateTypography state={audit.currentState} taskFormat="verb" />
                    </Box>
                </>
            )
        case 'approvalInstanceChange':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <AuditFormatMessage
                            taskType={task?.type?.taskType}
                            instance={audit?.typ.approvalInstanceChange?.instance}
                        />
                    </Typography>
                </>
            )
        case 'actionInstanceCreated': {
            const instance = audit.typ.actionInstanceCreated?.instance
            if (!instance) {
                return null
            }
            const executionId = instance.targetInstance?.oneofKind === 'automation' ? instance.targetInstance.automation?.automationExecutionId : undefined
            const executionIdNum = executionId ? parseInt(executionId, 10) : null
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline" component="span">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_action_instance_created"
                            defaultMessage="action execution started"
                        />
                        {executionIdNum && !isNaN(executionIdNum) && (
                            <>
                                {' '}
                                <ActionExecutionLink executionId={executionIdNum} />
                            </>
                        )}
                    </Typography>
                </>
            )
        }
        case 'actionInstanceSucceeded': {
            const instance = audit.typ.actionInstanceSucceeded?.instance
            if (!instance) {
                return null
            }
            const executionId = instance.targetInstance?.oneofKind === 'automation' ? instance.targetInstance.automation?.automationExecutionId : undefined
            const executionIdNum = executionId ? parseInt(executionId, 10) : null
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline" component="span">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_action_instance_succeeded"
                            defaultMessage="action execution completed successfully"
                        />
                        {executionIdNum && !isNaN(executionIdNum) && (
                            <>
                                {' '}
                                <ActionExecutionLink executionId={executionIdNum} />
                            </>
                        )}
                    </Typography>
                </>
            )
        }
        case 'actionInstanceFailed': {
            const instance = audit.typ.actionInstanceFailed?.instance
            if (!instance) {
                return null
            }
            const executionId = instance.targetInstance?.oneofKind === 'automation' ? instance.targetInstance.automation?.automationExecutionId : undefined
            const executionIdNum = executionId ? parseInt(executionId, 10) : null
            // Extract error message from action instance outcome
            const errorMessage = instance.outcome?.oneofKind === 'error'
                ? instance.outcome.error.errorMessage
                : undefined
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <UserDisplayName user={user} includeDetails={false} showTooltip />
                        <Typography sx={TaskAuditStyles.stepText} display="inline" component="span">
                            <FormattedMessage
                                id="component_task_details_audit_row_typ_action_instance_failed"
                                defaultMessage="action execution failed"
                            />
                            {executionIdNum && !isNaN(executionIdNum) && (
                                <>
                                    {' '}
                                    <ActionExecutionLink executionId={executionIdNum} />
                                </>
                            )}
                        </Typography>
                    </Box>
                    {errorMessage && (
                        <Typography variant="caption">
                            {errorMessage}
                        </Typography>
                    )}
                </Box>
            )
        }
        case 'stepUpApproval':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                        <Typography sx={TaskAuditStyles.stepText} display="inline">
                            <FormattedMessage
                                id="component_task_details_audit_row_typ_step_up_approval"
                                defaultMessage="step-up approval"
                            />
                        </Typography>
                        {hasStepUpTransactionPermission && (
                            <Box sx={{ mt: 1 }}>
                                <StepUpTransactionFetcher
                                    transactionId={audit.typ.stepUpApproval.stepUpTransactionId}
                                />
                            </Box>
                        )}
                    </Box>
                </>
            )
        case 'approvedAutomatically':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ"
                            defaultMessage="automatically approved access"
                        />
                    </Typography>
                </>
            )
        case 'actionSubmitted':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ"
                            defaultMessage="submitted action: {action}"
                            values={{
                                action:
                                    audit.typ.actionSubmitted.action?.actionType &&
                                    stringFromTaskAction(audit.typ.actionSubmitted.action.actionType),
                            }}
                        />
                    </Typography>
                </>
            )
        case 'connectorActionsStart':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_start"
                            defaultMessage="started applying access changes"
                        />
                    </Typography>
                </>
            )
        case 'certifyOutcome':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_certify_outcome"
                            defaultMessage="set task outcome: {certify}"
                            values={{
                                certify: CertifyOutcome[audit.typ.certifyOutcome.outcome].toLowerCase(),
                            }}
                        />
                    </Typography>
                </>
            )
        case 'provisionError':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_provision"
                            defaultMessage="stopped {action}: {err}"
                            values={{
                                err: audit.typ.provisionError.error.toLowerCase(),
                                action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovisioning' : 'provisioning',
                            }}
                        />
                    </Typography>
                </>
            )
        case 'provisionCancelled':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_provision_cancelled"
                            defaultMessage="provision cancelled{reason}"
                            values={{
                                reason: audit.typ.provisionCancelled.cancelReason
                                    ? ` (${audit.typ.provisionCancelled.cancelReason})`
                                    : '',
                            }}
                        />
                    </Typography>
                </>
            )
        case 'grantOutcome':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_grant_outcome"
                            defaultMessage="set task outcome: {grant}"
                            values={{
                                grant: GrantOutcome[audit.typ.grantOutcome.outcome].toLowerCase(),
                            }}
                        />
                    </Typography>
                </>
            )
        case 'waitStepSuccess':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_wait_step_success"
                            defaultMessage="confirmed the wait <condition>condition</condition>"
                            values={{
                                condition: (chunks) =>
                                    <Tooltip
                                        placement="top"
                                        title={audit.typ.oneofKind === 'waitStepSuccess' && audit.typ.waitStepSuccess.condition}
                                    >
                                        <a>
                                            {chunks}
                                        </a>
                                    </Tooltip>,
                            }}
                        />
                    </Typography>
                </>
            )
        case 'waitStepTimedOut':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_wait_step_timed_out"
                            defaultMessage="timed out the wait <condition>condition</condition>"
                            values={{
                                condition: (chunks) =>
                                    <Tooltip
                                        placement="top"
                                        title={audit.typ.oneofKind === 'waitStepTimedOut' && audit.typ.waitStepTimedOut.condition}
                                    >
                                        <a>
                                            {chunks}
                                        </a>
                                    </Tooltip>,
                            }}
                        />
                    </Typography>
                </>
            )
        case 'waitStepWaiting':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_wait_step_timed_out"
                            defaultMessage="is waiting for the <condition>condition</condition> to be true"
                            values={{
                                condition: (chunks) =>
                                    <Tooltip
                                        placement="top"
                                        title={audit.typ.oneofKind === 'waitStepWaiting' && audit.typ.waitStepWaiting.condition}
                                    >
                                        <a>
                                            {chunks}
                                        </a>
                                    </Tooltip>,
                            }}
                        />
                    </Typography>
                </>
            )
        case 'waitStepAnalysisWaiting':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_wait_step_analysis_waiting"
                            defaultMessage="waiting for analysis to complete"
                        />
                    </Typography>
                </>
            )
        case 'waitStepAnalysisSuccess':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_wait_step_analysis_success"
                            defaultMessage="analysis complete"
                        />
                    </Typography>
                </>
            )
        case 'waitStepAnalysisTimedOut':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_wait_step_analysis_timed_out"
                            defaultMessage="analysis timed out"
                        />
                    </Typography>
                </>
            )

        case 'webhookApprovalAttempt':
            return <>
                <UserDisplayName user={user} includeDetails={false} showTooltip />
                <Typography ml={1} display="inline" component="div">
                    <FormattedMessage
                        id="component_task_details_audit_row_typ_webhook_triggered"
                        defaultMessage="approval webhook attempted delivery using {webhook_name}: {webhook_url}"
                        values={{
                            webhook_name: audit.typ.webhookApprovalAttempt.webhookName,
                            webhook_url: audit.typ.webhookApprovalAttempt.webhookUrl,
                        }}
                    />
                </Typography>
            </>
        case 'webhookApprovalBadResponse':
            return <>
                <UserDisplayName user={user} includeDetails={false} showTooltip />
                <Typography ml={1} display="inline" component="div">
                    <FormattedMessage
                        id="component_task_details_audit_row_typ_webhook_triggered"
                        defaultMessage="approval webhook {webhook_name} responded with invalid data"
                        values={{
                            webhook_name: audit.typ.webhookApprovalBadResponse.webhookName,
                        }}
                    />
                </Typography>
            </>
        case 'webhookApprovalFatalError':
            return <>
                <UserDisplayName user={user} includeDetails={false} showTooltip />
                <Typography ml={1} display="inline" component="div">
                    <FormattedMessage
                        id="component_task_details_audit_row_typ_webhook_triggered"
                        defaultMessage="Webhook returned with a fatal error. Name: {webhook_name}, Instance ID: {webhook_instance_id}."
                        values={{
                            webhook_name: audit.typ.webhookApprovalFatalError.webhookName,
                            webhook_instance_id: audit.typ.webhookApprovalFatalError.webhookInstanceId,
                        }}
                    />
                </Typography>
            </>
        case 'webhookApprovalTriggered':
            return <>
                <UserDisplayName user={user} includeDetails={false} showTooltip />
                <Typography ml={1} display="inline" component="div">
                    <FormattedMessage
                        id="component_task_details_audit_row_typ_webhook_triggered"
                        defaultMessage="approval webhook trigger using {webhook_name}: {webhook_url}"
                        values={{
                            webhook_name: audit.typ.webhookApprovalTriggered.webhookName,
                            webhook_url: audit.typ.webhookApprovalTriggered.webhookUrl,
                        }}
                    />
                </Typography>
            </>
        case 'webhookApprovalSuccess':
            return <>
                <UserDisplayName user={user} includeDetails={false} showTooltip />
                <Typography ml={1} display="inline" component="div">
                    <FormattedMessage
                        id="component_task_details_audit_row_typ_webhook_triggered"
                        defaultMessage="approved webhook responded successfully using {webhook_name}: {webhook_url}"
                        values={{
                            webhook_name: audit.typ.webhookApprovalSuccess.webhookName,
                            webhook_url: audit.typ.webhookApprovalSuccess.webhookUrl,
                        }}
                    />
                </Typography>
            </>
        case 'actionResult': {
            const {
                data: entitlement, error: entitlementErr,
            } = getAppEntitlementApi.useGet({
                appId: audit.typ.actionResult.appId,
                id: audit.typ.actionResult.appEntitlementId,
                expandMask: AppEntitlementExpandMask.create({ paths: ['*'] }),
            }, { actionable: !!(audit.typ.actionResult.appId && audit.typ.actionResult.appEntitlementId) })

            const oneOfKind = audit.typ.actionResult.result.oneofKind
            switch (oneOfKind) {
                case 'success': {
                    return <>
                        <UserDisplayName user={user} includeDetails={false} showTooltip />
                        <Typography sx={TaskAuditStyles.stepText} display="inline">
                            <FormattedMessage
                                id="component_task_details_audit_row_typ_result_outcome_err"
                                defaultMessage="{action} entitlement"
                                values={{
                                    action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovision' : 'provision',
                                }}
                            />
                        </Typography>
                        <AuditEntitlementDisplay entitlementErr={entitlementErr} entitlement={entitlement} />
                    </>
                }
                case 'cancelled': {
                    return <>
                        <Stack alignItems="left">
                            <UserDisplayName user={user} includeDetails={false} showTooltip />
                            <AuditEntitlementDisplay entitlementErr={entitlementErr} entitlement={entitlement} topPadding />
                        </Stack>
                        <Typography sx={TaskAuditStyles.stepText} display="inline">
                            <FormattedMessage
                                id="component_task_details_audit_row_typ_result_outcome_err"
                                defaultMessage="{action} cancelled"
                                values={{
                                    action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovision' : 'provision',
                                }}
                            />
                        </Typography>
                    </>
                }
                case 'error': {
                    const error = audit.typ.actionResult.result.error
                    if (error.errorReason) {
                        return <>
                            <Stack alignItems="left">
                                <UserDisplayName user={user} includeDetails={false} showTooltip />
                                <AuditEntitlementDisplay entitlementErr={entitlementErr} entitlement={entitlement} topPadding />
                            </Stack>
                            <Typography sx={TaskAuditStyles.stepText} display="inline">
                                <FormattedMessage
                                    id="component_task_details_audit_row_typ_result_outcome_err"
                                    defaultMessage="error using connector {action} [attempt #{err_count}]: {err_reason}"
                                    values={{
                                        err_count: error.errorCount,
                                        err_reason: error.errorReason,
                                        action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovision' : 'provision',
                                    }}
                                />
                            </Typography>
                        </>
                    }
                    return <>
                        <UserDisplayName user={user} includeDetails={false} showTooltip />
                        <Typography sx={TaskAuditStyles.stepText} display="inline">
                            <FormattedMessage
                                id="component_task_details_audit_row_typ_result_outcome_err"
                                defaultMessage="error occurred during {action}"
                                values={{
                                    action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovision' : 'provision',
                                }}
                            />
                        </Typography>
                    </>
                }
                case undefined:
                    if (process.env.NODE_ENV === 'development') {
                        throw new Error('audit.typ.actionResult.result.oneofKind is undefined')
                    }
                    return null
                default: {
                    // A new action result was added to the API but not handled here
                    const exhaustiveCheck: never = oneOfKind
                    throw new Error(`Unhandled audit event type case: ${exhaustiveCheck}`)
                }
            }
        }
        case 'connectorActionsEnd':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ"
                            defaultMessage="finished processing request"
                        />
                    </Typography>
                </>
            )
        case 'taskCreated':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ"
                            defaultMessage="created {emergency_access} task"
                            values={{ emergency_access: task?.emergencyAccess ? 'emergency access' : '' }}
                        />
                    </Typography>
                </>
            )
        case 'approvalReassigned':
            return (
                <Stack direction="row" alignItems="center">
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Box sx={TaskAuditStyles.reassignStepTextContainer}>
                        <Typography sx={TaskAuditStyles.stepText} display="inline">
                            <FormattedMessage
                                id="component_task_details_audit_row_typ_reassigned"
                                defaultMessage="reassigned approval step to: "
                            />
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 1 }}>
                        <C1MultiUserDisplay users={audit.typ.approvalReassigned.users} />
                    </Box>
                </Stack>
            )
        case 'provisionReassigned':
            return (
                <Stack direction="row" alignItems="center">
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Box sx={TaskAuditStyles.reassignStepTextContainer}>
                        <Typography sx={TaskAuditStyles.stepText} display="inline">
                            <FormattedMessage
                                id="component_task_details_audit_row_typ_reassigned_provision"
                                defaultMessage="reassigned {action} step to: "
                                values={{
                                    action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovision' : 'provision',
                                }}
                            />
                        </Typography>
                    </Box>
                    <Box>
                        <C1MultiUserDisplay users={audit.typ.provisionReassigned.users} />
                    </Box>
                </Stack>
            )
        case 'reassignmentFallbackToAdmin':
            return (
                <Stack direction="row" alignItems="center">
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Box sx={TaskAuditStyles.reassignStepTextContainer}>
                        <Typography sx={TaskAuditStyles.stepText} display="inline">
                            <FormattedMessage
                                id="component_task_details_audit_row_typ_fallback_to_admin"
                                defaultMessage="no eligible reviewers found, falling back to system administrators: "
                            />
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 1 }}>
                        <C1MultiUserDisplay users={audit.typ.reassignmentFallbackToAdmin.adminUsers} />
                    </Box>
                </Stack>
            )
        case 'taskRestarted':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ"
                            defaultMessage="restarted the policy"
                        />
                    </Typography>
                </>
            )
        case 'taskEscalated':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ"
                            defaultMessage="escalated the policy"
                        />
                    </Typography>
                </>
            )
        case 'hardReset':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ"
                            defaultMessage="hard reset the policy"
                        />
                    </Typography>
                </>
            )
        case 'accessRequestOutcome':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_certify_outcome"
                            defaultMessage="set task outcome: {access_request}"
                            values={{
                                access_request:
                                    AccessRequestOutcome[audit.typ.accessRequestOutcome.outcome].toLowerCase(),
                            }}
                        />
                    </Typography>
                </>
            )
        case 'reassignedToDelegate':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_reassigned_to_delegate"
                            defaultMessage="delegated task from {original_user} to {delegated_user}"
                            values={{
                                original_user: (
                                    <UserDisplayName
                                        user={audit.typ.reassignedToDelegate.originalAssigneeUser}
                                        includeDetails={false}
                                        showTooltip
                                    />
                                ),
                                delegated_user: (
                                    <UserDisplayName
                                        user={audit.typ.reassignedToDelegate.delegatedAssigneeUser}
                                        includeDetails={false}
                                        showTooltip
                                    />
                                ),
                            }}
                        />
                    </Typography>
                </>
            )
        case 'conditionalPolicyExecutionResult': {
            const result = audit.typ.conditionalPolicyExecutionResult

            let icon = <C1Icon icon={Ban} size="sm" />
            let conditionResult = <FormattedMessage
                id="component_task_details_audit_row_typ_policy_execution_result_did_not_match"
                defaultMessage="did not match"
            />

            if (result.conditionMatched) {
                icon = <C1Icon icon={CircleCheck} size="sm" />
                conditionResult = <FormattedMessage
                    id="component_task_details_audit_row_typ_policy_execution_result_matched"
                    defaultMessage="matched"
                />
            }

            if (result.error) {
                icon = <C1Icon icon={CircleAlert} size="sm" sx={TaskAuditStyles.errorIcon} />
                conditionResult = <Typography variant="monospace">
                    {result.error}
                </Typography>
            }

            const expression = !result.defaultCondition &&
                <Box
                    sx={{
                        borderRadius: C1BorderRadius.large,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        mt: 1,
                        p: 2,
                    }}
                >
                    <Typography variant="monospace">
                        {result.condition}
                    </Typography>
                </Box>

            return <Typography component="div">
                { /* eslint-disable-next-line react/jsx-one-expression-per-line */}
                {icon} {result.defaultCondition && 'default'} policy rule {conditionResult}
                {expression}
            </Typography>
        }
        case 'expressionPolicyStepError': {
            const err = audit.typ.expressionPolicyStepError.error
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_expression_policy_step_error"
                            defaultMessage="policy step expression evaluation error: {err}"
                            values={{
                                err: err.toLowerCase(),
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'approvalAutoAcceptedByPolicy': {
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_approval_auto_accepted_by_policy"
                            defaultMessage="approval auto accepted by policy"
                        />
                    </Typography>
                </>
            )
        }
        case 'approvalAutoRejectedByPolicy': {
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_approval_auto_rejected_by_policy"
                            defaultMessage="approval auto rejected by policy"
                        />
                    </Typography>
                </>
            )
        }
        case 'revokeOutcome':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_revoke_outcome"
                            defaultMessage="set task outcome: {revoke}"
                            values={{
                                revoke: RevokeOutcome[audit.typ.revokeOutcome.outcome].toLowerCase(),
                            }}
                        />
                    </Typography>
                </>
            )
        case 'bulkActionError': {
            const err = audit.typ.bulkActionError.error

            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_expression_policy_step_error"
                            defaultMessage="modified task in a bulk action, which errored: {err}"
                            values={{
                                err: err.toLowerCase(),
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'webhookTriggered':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_webhook_triggered"
                            defaultMessage="triggered {action} via webhook using {webhook_name}: {webhook_url}"
                            values={{
                                webhook_name: audit.typ.webhookTriggered.webhookName,
                                webhook_url: audit.typ.webhookTriggered.webhookUrl,
                                action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovisioning' : 'provisioning',
                            }}
                        />
                    </Typography>
                </>
            )
        case 'webhookAttempt':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_webhook_attempt"
                            defaultMessage="attempted {action} via webhook using {webhook_name}: {webhook_url}"
                            values={{
                                webhook_name: audit.typ.webhookAttempt.webhookName,
                                webhook_url: audit.typ.webhookAttempt.webhookUrl,
                                action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovisioning' : 'provisioning',
                            }}
                        />
                    </Typography>
                </>
            )
        case 'webhookSuccess':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_webhook_success"
                            defaultMessage="successfully {action} via webhook using {webhook_name}: {webhook_url}"
                            values={{
                                action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovisioned' : 'provisioned',
                                webhook_name: audit.typ.webhookSuccess.webhookName,
                                webhook_url: audit.typ.webhookSuccess.webhookUrl,
                            }}
                        />
                    </Typography>
                </>
            )
        case 'policyEvaluationStep':
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        {audit.typ.policyEvaluationStep.stepComment}
                    </Typography>
                </>
            )
        case 'externalTicketTriggered': {
            const appId = audit.typ.externalTicketTriggered.appId
            const connectorId = audit.typ.externalTicketTriggered.connectorId
            const externalTicketProvisionerConfigId = audit.typ.externalTicketTriggered.externalTicketProvisionerConfigId
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_external_ticket_triggered"
                            defaultMessage="triggered {action} via external ticket using configured external ticket provisioner {external_ticket_provisioner_config_link}"
                            values={{
                                external_ticket_provisioner_config_link: <C1Link
                                    href={routes.settings.externalTicketing.detail(appId, connectorId, externalTicketProvisionerConfigId)}
                                >
                                    {audit.typ.externalTicketTriggered.externalTicketProvisionerConfigName}
                                </C1Link>,
                                action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovisioning' : 'provisioning',
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'externalTicketCreated': {
            const appId = audit.typ.externalTicketCreated.appId
            const connectorId = audit.typ.externalTicketCreated.connectorId
            const externalTicketProvisionerConfigId = audit.typ.externalTicketCreated.externalTicketProvisionerConfigId
            const externalTicketUrl = audit.typ.externalTicketCreated.externalTicketUrl
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_external_ticket_created"
                            defaultMessage="created {external_ticket_link} using configured external ticket provisioner {external_ticket_provisioner_config_link}"
                            values={{
                                external_ticket_link: <ExternalLink
                                    href={externalTicketUrl}
                                >
                                    external ticket
                                </ExternalLink>,
                                external_ticket_provisioner_config_link: <C1Link
                                    href={routes.settings.externalTicketing.detail(appId, connectorId, externalTicketProvisionerConfigId)}
                                >
                                    {audit.typ.externalTicketCreated.externalTicketProvisionerConfigName}
                                </C1Link>,
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'externalTicketProvisionStepResolved': {
            const externalTicketUrl = audit.typ.externalTicketProvisionStepResolved.externalTicketUrl
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_external_ticket_provision_step_resolved"
                            defaultMessage="resolved {action} step for {external_ticket_link}"
                            values={{
                                external_ticket_link: <ExternalLink
                                    href={externalTicketUrl}
                                >
                                    external ticket
                                </ExternalLink>,
                                action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovision' : 'provision',
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'externalTicketError': {
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        {audit.typ.externalTicketError.errorMessage}
                    </Typography>
                </>
            )
        }
        case 'stepSkipped': {
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_step_skipped"
                            defaultMessage="skipped step"
                        />
                    </Typography>
                </>
            )
        }
        case 'reassignmentListError': {
            return <>
                <UserDisplayName user={user} includeDetails={false} showTooltip />
                <Typography ml={1} display="inline">
                    {audit.typ.reassignmentListError.errorMessage}
                </Typography>
            </>
        }
        case 'slaEscalation': {
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_sla_escalation"
                            defaultMessage="SLA not met. {message}"
                            values={{
                                message: audit.typ.slaEscalation.message,
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'policyChanged': {
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_policy_changed"
                            defaultMessage="changed policy"
                        />
                    </Typography>
                </>
            )
        }
        case 'formInstanceChange': {
            const isValid = audit.typ.formInstanceChange.isValid
            const validationStatus = isValid ? <FormattedMessage
                id="component_task_details_audit_row_typ_form_instance_valid"
                defaultMessage="valid"
            /> : <FormattedMessage
                id="component_task_details_audit_row_typ_form_instance_invalid"
                defaultMessage="invalid"
            />

            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_form_instance_change"
                            defaultMessage="updated task data, data is {validation_status}"
                            values={{
                                validation_status: validationStatus,
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'grantDurationUpdated': {
            const duration = audit.typ.grantDurationUpdated.duration
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_grant_duration_updated"
                            defaultMessage="updated grant duration to {duration}"
                            values={{
                                duration: <RoundedDuration duration={duration} />,
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'waitStepUntilTime': {
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_wait_step_until_time"
                            defaultMessage="waited until {until_time}"
                            values={{
                                until_time: audit.typ.waitStepUntilTime.untilTime
                                    ? pbtsTimestamp.toDate(audit.typ.waitStepUntilTime.untilTime).toLocaleString()
                                    : '',
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'accountLifecycleActionCreated': {
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_account_lifecycle_action_created"
                            defaultMessage="started {action} via lifecycle action: {action_name}"
                            values={{
                                action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovisioning' : 'provisioning',
                                action_name: audit.typ.accountLifecycleActionCreated.batonActionDisplayName,
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'accountLifecycleActionFailed': {
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography ml={1} display="inline" component="div">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_account_lifecycle_action_failed"
                            defaultMessage="Failed to {action} via lifecycle action: {action_name}: {error_msg}"
                            values={{
                                action: task.type?.taskType.oneofKind === 'revoke' ? 'deprovision' : 'provision',
                                action_name: audit.typ.accountLifecycleActionFailed.batonActionDisplayName,
                                error_msg: audit.typ.accountLifecycleActionFailed.error,
                            }}
                        />
                    </Typography>
                </>
            )
        }
        case 'createdReplacementExtensionGrantTask': {
            const { newTaskNumericId } = audit.typ.createdReplacementExtensionGrantTask
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_created_replacement_extension_grant_task"
                            defaultMessage="created a new task"
                        />
                    </Typography>
                    <Box sx={{ ml: 1 }}>
                        <C1Link href={routes.task(String(newTaskNumericId))} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    borderRadius: 1,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 1,
                                    px: 1,
                                    backgroundColor: 'background.default',
                                    '&:hover': { borderColor: 'grey.500' },
                                }}
                            >
                                <GrantTaskIcon />
                                <Typography component="span">{newTaskNumericId}</Typography>
                            </Box>
                        </C1Link>
                    </Box>
                </>
            )
        }
        case 'taskCreatedFrom': {
            const { originalTaskNumericId } = audit.typ.taskCreatedFrom
            const taskBadge = (
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.primary',
                        borderRadius: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        px: 1,
                        backgroundColor: 'background.default',
                        ...(originalTaskNumericId ? { '&:hover': { borderColor: 'grey.500' } } : {}),
                    }}
                >
                    <GrantTaskIcon />
                    <Typography component="span">{originalTaskNumericId || 'Unknown'}</Typography>
                </Box>
            )
            return (
                <>
                    <UserDisplayName user={user} includeDetails={false} showTooltip />
                    <Typography sx={TaskAuditStyles.stepText} display="inline">
                        <FormattedMessage
                            id="component_task_details_audit_row_typ_task_created_from"
                            defaultMessage="created a new task from"
                        />
                    </Typography>
                    <Box sx={{ ml: 1 }}>
                        {originalTaskNumericId ? (
                            <C1Link href={routes.task(String(originalTaskNumericId))} style={{ textDecoration: 'none', color: 'inherit' }}>
                                {taskBadge}
                            </C1Link>
                        ) : (
                            taskBadge
                        )}
                    </Box>
                </>
            )
        }
        case undefined:
            return null
        default: {
            const exhaustiveCheck: never = eventType
            throw new Error(`Unhandled audit event type case: ${exhaustiveCheck}`)
        }
    }
}

const isAuditError = (audit: TaskAuditView): boolean => audit.eventType === TaskAuditEventType.ERROR

const cols: C1TableReduxColumn<TaskAuditView, Task>[] = [
    {
        field: 'event',
        flex: 5,
        label: <FormattedMessage id="audit_log_event_detail" defaultMessage="Event detail" />,
        Component: ({ row, extras: task }): React.ReactElement => {
            if (row.typ.oneofKind === 'comment') {
                return <></>
            }
            return <AuditEvent audit={row} task={task} />
        },
    },
    {
        field: 'date',
        maxWidth: '300px',
        label: <FormattedMessage id="audit_log_event_date" defaultMessage="Date" />,
        Component: ({ row }): React.ReactElement => {
            if (row.typ.oneofKind === 'comment') {
                return <></>
            }
            return <RelativeTimestamp variant="dense" date={row.created} typographyVariant="body1" />
        },
    },
]

export const TaskAuditV2: FC<{
    taskId: string
    task: TaskValue
}> = ({ taskId, task }) => {
    const [page, setPage] = useState<number>(0)

    // TODO Santhosh unary list collect?
    const pageSize = 100
    const { data, isLoading, isError, error, canLoadMore, getPage } = TaskAuditApi.useList(TaskAuditListRequest.create({
        taskId,
    }), {
        evictionAfterSeconds: 0,
        notify: true,
        autoReload: true,
        pageSize,
    })

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const filteredData: TaskAuditView[][] = useMemo(() => [], [])
    data.forEach((audits, index) => {
        filteredData[index] = audits
            .slice()
            .sort(dateSort)
            .filter((a) => a.typ.oneofKind !== 'comment')
    })
    const table = useMemo(
        () => (
            <C1TableRedux<TaskAuditView, Task>
                data={filteredData}
                cols={cols}
                canLoadMore={canLoadMore}
                isError={isError}
                isLoading={isLoading}
                pageSize={pageSize}
                onPageChange={onPageChange}
                error={error}
                page={page}
                staticExtraProps={task}
                isRowError={isAuditError}
            />
        ),
        [filteredData, canLoadMore, isError, isLoading, onPageChange, error, page, task],
    )

    return <Box sx={TaskAuditStyles.container}>
        <List<TaskAuditView> table={table} />
    </Box>
}
